import { IdentityPlatform, PlaycastGamepadDevice, XInputValues } from '@playcastdotio/CoreTypes';
import { useEffect, useState } from 'react';

export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
};

export const isSafari = () => {
  return navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome') && !navigator.userAgent.includes('Chromium');
};

export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Largest iPhone is 440x956 CSS px; smallest iPad Apple still ships is 744x1133. A threshold
 * between the two is the only way to tell an iPad apart from an iPhone in "Request Desktop
 * Website" mode, because both report the identical macOS Safari user agent with touch points.
 */
export const IPAD_MIN_SCREEN_DIMENSION_PX = 1000;

/** Largest screen dimension in CSS px, or 0 when the browser will not report one. */
const largestScreenDimensionPx = (): number => {
  const screen = typeof window === 'undefined' ? undefined : window.screen;
  const width = typeof screen?.width === 'number' ? screen.width : 0;
  const height = typeof screen?.height === 'number' ? screen.height : 0;
  return Math.max(width, height);
};

/**
 * An iPad running iPadOS 13 or later.
 *
 * iPadOS reports the macOS Safari user agent by default, so `/ipad/` never matches on a modern
 * iPad and the device used to classify as a desktop while an equivalent Android tablet — whose
 * UA still says `Android` — classified as mobile. Apple ships no touchscreen Mac, so "Mac user
 * agent plus touch points" is an iOS tablet or an iPhone asking for the desktop site; the
 * screen size separates those two.
 *
 * Fails to today's behaviour: if the browser reports no screen dimensions, this returns false
 * and the device is classified exactly as it was before.
 */
export const isIPadOSDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  const isMacUA = /macintosh|mac os x/.test(userAgent);
  if (!isMacUA || !(navigator.maxTouchPoints > 0)) return false;
  return largestScreenDimensionPx() >= IPAD_MIN_SCREEN_DIMENSION_PX;
};

/**
 * Whether this session should be treated as a mobile device.
 *
 * The classification is deliberately platform-symmetric: every rule below is stated in terms
 * of evidence the device gives (user-agent markers, touch, screen size, viewport width) and
 * none of it in terms of the vendor. An earlier version rescued a narrow-viewport session only
 * when `isAppleDevice()` agreed, which meant an Android phone in "Request Desktop Site" mode
 * fell through to desktop while an iPhone in the same mode did not.
 *
 * Precedence:
 *  1. A UA that still names a handset or tablet (including an iPadOS `Macintosh` UA) — mobile.
 *  2. Touch with a phone-sized screen — mobile even in desktop-site mode or landscape.
 *  3. Touch at a phone-sized viewport — mobile when stronger device evidence is absent.
 *  4. No touch — desktop.
 */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isTouch = isTouchDevice();

  // Handsets that still name themselves in the user agent.
  const isMobileUA = /iphone|ipod|android|blackberry|windows phone/.test(userAgent);

  // Tablets. An Android tablet says `Android`; a modern iPad says `Macintosh` and has to be
  // recognised by its touch points and screen size instead.
  const isTablet =
    /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/.test(userAgent) || isIPadOSDevice();

  const hasMobileIndicator = isMobileUA || isTablet;

  // Viewport at or below the mobile breakpoint (Tailwind's md).
  const isNarrowViewport = window.matchMedia('(max-width: 768px)').matches;

  // Desktop-site mode changes the layout viewport and UA, not the device's screen.
  // Use its shorter dimension so rotating a phone cannot switch its stream to desktop.
  const { width, height } = window.screen;
  const hasPhoneScreen = Number.isFinite(width) && Number.isFinite(height)
    && width > 0 && height > 0 && Math.min(width, height) <= 768;

  // A touch device laid out at a phone-sized viewport is a phone whatever its UA claims, and
  // that is true on Android exactly as it is on iOS.
  return isTouch && (hasMobileIndicator || hasPhoneScreen || isNarrowViewport);
};

/**
 * React hook version of isMobileDevice that reactively updates when viewport changes
 * This ensures components re-render when users request "Desktop Site" on mobile browsers
 *
 * Use this for: mobile gamepad visibility, input method detection, device capability checks
 */
export const useIsMobileDevice = (): boolean => {
  const [isMobile, setIsMobile] = useState(() => isMobileDevice());

  useEffect(() => {
    // Check if window is defined (for SSR compatibility)
    if (typeof window === 'undefined') return;

    // Handler for window resize - update mobile detection
    const handleResize = () => {
      const newIsMobile = isMobileDevice();
      setIsMobile(newIsMobile);
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Also listen for orientation changes (mobile specific)
    window.addEventListener('orientationchange', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return isMobile;
};

/**
 * Simple viewport-based mobile detection (responsive design approach)
 * Only checks if viewport width is below 768px (Tailwind's md breakpoint)
 *
 * Use this for: responsive UI layouts, CSS/styling decisions, simple viewport-based logic
 * Note: This does NOT detect actual device type or "Request Desktop Site"
 */
export const useIsMobileViewport = (): boolean => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if window is defined (for SSR compatibility)
    if (typeof window === 'undefined') return;

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // Tailwind's md breakpoint
    };

    // Check on mount
    checkMobile();

    // Listen for window resize
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

export const getKeyboardKeyLocation = (location: number): 'standard' | 'left' | 'right' | 'numpad' => {
  switch (location) {
    case 0:
      return 'standard';
    case 1:
      return 'left';
    case 2:
      return 'right';
    case 3:
      return 'numpad';
    default:
      return 'standard';
  }
};

export const getMouseButton = (button: number): 'back' | 'forward' | 'left' | 'middle' | 'right' => {
  switch (button) {
    case 0:
      return 'left';
    case 1:
      return 'middle';
    case 2:
      return 'right';
    case 3:
      return 'back';
    default:
      return 'forward';
  }
};

export const isAppleDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for macOS
  const isMac = /macintosh|mac os x/.test(userAgent);

  // Check for Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Check for iPad that identifies as MacOS in newer versions
  const isIPadOS = isMac && navigator.maxTouchPoints > 0;

  return isIOS || isMac || isIPadOS || isSafari;
};

/**
 * Check if the user is on a non-Windows operating system
 * Returns true for Mac, Linux, Android, iOS, iPad, etc.
 * Used to determine if download should be blocked (Windows-only app)
 */
export const isNonWindowsOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  const userAgent = window.navigator.userAgent.toLowerCase();
  return (
    userAgent.includes('mac') ||
    userAgent.includes('linux') ||
    userAgent.includes('android') ||
    userAgent.includes('iphone') ||
    userAgent.includes('ipad')
  );
};

export const getPlatformFromPath = (path: string): IdentityPlatform => {
  // the url is formatted as /jx/:joinId
  const jx = path.split('/')[1];
  if (jx === 'j') {
    return IdentityPlatform.CLERK;
  }
  if (jx === 'jd') {
    return IdentityPlatform.DISCORD;
  }
  if (jx === 'jt') {
    return IdentityPlatform.TWITCH;
  }
  throw new Error('Invalid platform');
};

// Browser capture of gamepad sticks use y axis inverted from XInput and WebGL
const toShort = (raw: number, reverse: boolean) => (((reverse ? 0 - raw : raw) + 1) * 32767.5 - 32768) | 0;

// Gamepad triggers are 0 to 1, XInput triggers are 0 to 255
const toByte = (raw: number) => (raw * 255) | 0;

const toWord = (buttons: GamepadButton[]) =>
  (buttons[12]?.pressed ? XInputValues.DPAD_UP : 0) |
  (buttons[13]?.pressed ? XInputValues.DPAD_DOWN : 0) |
  (buttons[14]?.pressed ? XInputValues.DPAD_LEFT : 0) |
  (buttons[15]?.pressed ? XInputValues.DPAD_RIGHT : 0) |
  (buttons[9]?.pressed ? XInputValues.START : 0) |
  (buttons[8]?.pressed ? XInputValues.BACK : 0) |
  (buttons[10]?.pressed ? XInputValues.LEFT_THUMB : 0) |
  (buttons[11]?.pressed ? XInputValues.RIGHT_THUMB : 0) |
  (buttons[4]?.pressed ? XInputValues.LEFT_SHOULDER : 0) |
  (buttons[5]?.pressed ? XInputValues.RIGHT_SHOULDER : 0) |
  (buttons[0]?.pressed ? XInputValues.A : 0) |
  (buttons[1]?.pressed ? XInputValues.B : 0) |
  (buttons[2]?.pressed ? XInputValues.X : 0) |
  (buttons[3]?.pressed ? XInputValues.Y : 0);

export const convertGamepads = (gamepads: Gamepad[]) => {
  const raw = gamepads.map((g) => {
    return {
      axes: g.axes,
      buttons: g.buttons.map((b: GamepadButton) => {
        return {
          pressed: b.pressed,
          touched: b.touched,
          value: b.value,
        };
      }),
      id: g.id,
      index: g.index,
      mapping: g.mapping,
      timestamp: g.timestamp,
    };
  });

  return raw.map((r) => {
    const output: PlaycastGamepadDevice = {
      deviceId: r.index,
      id: r.id,
      timestamp: r.timestamp,
      xInput: {
        wButtons: toWord(r.buttons),
        bLeftTrigger: toByte(r.buttons[6]?.value ?? 0),
        bRightTrigger: toByte(r.buttons[7]?.value ?? 0),
        sThumbLX: toShort(r.axes[0], false),
        sThumbLY: toShort(r.axes[1], true),
        sThumbRX: toShort(r.axes[2], false),
        sThumbRY: toShort(r.axes[3], true),
      },
    };

    return output;
  });
};

